/**
 * Générateur — Attendu 654 — Milieu d'un segment
 * Niveau : 6ème | Cluster : 29 | Domaine : Espace et géométrie
 *
 * 5 exercices / 5 formats :
 *   - definition_vf_tableau           : V/F sur la définition du milieu (conjonction M ∈ [AB] ET MA = MB)
 *   - demilongueur_saisie             : calculer AM = AB/2 (3 régimes : cm entier, cm décimal, conversion cm→mm)
 *   - reconnaitre_milieu_qcm_unique   : QCM 4 choix invariants, 3 scénarios (milieu, médiatrice, point quelconque)
 *   - placement_milieu_qcm_images     : QCM 4 images SVG (milieu / tiers / 2 tiers / médiatrice hors segment)
 *   - construire_compas_programme     : programme_construction (3 tâches : arc A, arc B, placement M)
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_654.md et docs/fiches/PAYLOADS_654.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeProgrammeConstruction,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 654;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEFINITION = 'Définition du milieu (Vrai/Faux)';
const L_DEMILONGUEUR = 'Calculer la demi-longueur';
const L_RECONNAITRE = 'Reconnaître le milieu';
const L_PLACEMENT = 'Identifier le placement du milieu';
const L_CONSTRUIRE_COMPAS = 'Construire le milieu au compas';

// Lettres valides pour nommer les points (exclut I et O : ambiguïté avec 1 et 0).
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');
// M est réservé au point milieu dans TOUS les formats (écrit en dur dans les
// pools, les énoncés, les labels SVG et les tâches de construction). On l'exclut
// donc des extrémités partout : sans ce filtre, on produisait des segments [EM]
// ou [MF] où la même lettre désignait deux points, jusqu'à des énoncés du type
// « M est le milieu de [EM] et EM = 9 cm. Quelle est la longueur EM ? ».
const LETTRES_SANS_M = LETTRES.filter(l => l !== 'M');

// Constantes SVG (programme_construction — alignées avec gen_patron.js)
const PX_PER_CM = 30;
const VIEWBOX_PC = { w: 600, h: 400 };
const TOLERANCE_PC = 12;

// Couleurs pour les tâches
const COLOR_BLEU = '#378ADD';
const COLOR_ROUGE = '#E24B4A';
const COLOR_VERT = '#1D9E75';
const COLOR_ORANGE = '#F97316';

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Encode une chaîne SVG en base64, compatible navigateur + Node. */
const svgToBase64 = (svg) => {
    if (typeof btoa === 'function') {
        try { return btoa(unescape(encodeURIComponent(svg))); } catch (_e) { /* fallthrough */ }
    }
    return Buffer.from(svg, 'utf-8').toString('base64');
};

/** Formate un nombre à la française (virgule décimale). */
const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    return String(n).replace('.', ',');
};

/** Tire n lettres distinctes dans LETTRES. */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Définition du milieu (V/F tableau)
// ═══════════════════════════════════════════════════════════

/** Pool des affirmations VRAIES (sur un segment [AB] et un point M). */
const POOL_VRAIES = (A, B, n) => [
    `Si M ∈ [${A}${B}] et M${A} = M${B}, alors M est le milieu de [${A}${B}]`,
    `Le milieu de [${A}${B}] vérifie M${A} = M${B} = ${A}${B}/2`,
    `Le milieu de [${A}${B}] appartient à [${A}${B}]`,
    `Si M est le milieu de [${A}${B}], alors M${A} = M${B}`,
    `Le milieu de [${A}${B}] est unique`,
    `Si M est le milieu de [${A}${B}] et ${A}${B} = ${n} cm, alors ${A}M = ${fmt(n / 2)} cm`,
];

/** Pool des affirmations FAUSSES (contre-exemples cognitifs). */
const POOL_FAUSSES = (A, B, _n) => [
    `Tout point vérifiant M${A} = M${B} est le milieu de [${A}${B}]`, // piège médiatrice
    `Un segment peut avoir deux milieux différents`,                   // piège unicité
    `Tout point de [${A}${B}] est le milieu de [${A}${B}]`,            // piège appartenance seule
    `Si M${A} = M${B} alors M ∈ [${A}${B}]`,                           // réciproque fausse
    `Le milieu de [${A}${B}] est le point le plus éloigné de ${A} et ${B}`, // contre-sens
    `Si ${A}M = ${A}${B} alors M est le milieu de [${A}${B}]`,         // piège égalité fausse
];

const genDefinitionVF = () => {
    const [A, B] = pickN(2, LETTRES_SANS_M);
    // n entier pair pour que n/2 soit propre dans la vraie n°6 (sinon on force un pair).
    const n = randomChoice([4, 6, 8, 10, 12, 14, 16, 18]);

    const vraies = POOL_VRAIES(A, B, n);
    const fausses = POOL_FAUSSES(A, B, n);

    // Composition stricte : 3 vraies + 3 fausses (conforme PAYLOADS_654.md).
    const chosenVraies = randomChoiceMultiple(vraies, 3);
    const chosenFausses = randomChoiceMultiple(fausses, 3);

    const merged = shuffle([
        ...chosenVraies.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenFausses.map(l => ({ ligne: l, reponse: 1 })),
    ]);

    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    // Consigne réduite au cadre : les colonnes Vrai/Faux disent déjà quoi faire.
    // « M est un point » reste explicite — M quelconque EST le ressort de
    // l'exercice, l'élève doit décider au cas par cas s'il peut être le milieu.
    const texte = `<p>[${A}${B}] est un segment et M est un point.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_DEFINITION));
    payload._meta.generatorKey = 'definition_vf_tableau';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Calculer la demi-longueur (saisie)
// ═══════════════════════════════════════════════════════════

const genDemilongueurSaisie = () => {
    const [A, B] = pickN(2, LETTRES_SANS_M);
    const regime = randomChoice(['entier', 'decimal', 'conversion']);

    let ab, bonneReponse, unite, texte;

    if (regime === 'entier') {
        // AB pair entre 4 et 18 cm → AM entier
        ab = randomChoice([4, 6, 8, 10, 12, 14, 16, 18]);
        bonneReponse = fmt(ab / 2);
        unite = 'cm';
        texte = `<p>M est le milieu du segment [${A}${B}] et ${A}${B} = ${ab} cm.</p><p>Quelle est la longueur ${A}M, en cm ?</p>`;
    } else if (regime === 'decimal') {
        // AB impair entre 3 et 17 cm → AM demi-entier
        ab = randomChoice([3, 5, 7, 9, 11, 13, 15, 17]);
        bonneReponse = fmt(ab / 2);
        unite = 'cm';
        texte = `<p>M est le milieu du segment [${A}${B}] et ${A}${B} = ${ab} cm.</p><p>Quelle est la longueur ${A}M, en cm ?</p>`;
    } else {
        // Conversion cm → mm : AB entier entre 3 et 17 cm, réponse en mm (multiplier ab/2 par 10)
        ab = randInt(3, 17);
        const am_mm = Math.round((ab / 2) * 10); // arrondi explicite pour éviter toute trace de flottant
        bonneReponse = fmt(am_mm);
        unite = 'mm';
        texte = `<p>M est le milieu du segment [${A}${B}] et ${A}${B} = ${ab} cm.</p><p>Quelle est la longueur ${A}M, en <strong>mm</strong> ?</p>`;
    }

    const payload = makeSaisie(bonneReponse, texte, {
        ...opts(L_DEMILONGUEUR),
        suffixe: ` ${unite}`,
    });
    payload._meta.generatorKey = 'demilongueur_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Reconnaître le milieu (QCM 4 choix invariants)
// ═══════════════════════════════════════════════════════════

/**
 * Les 4 options sont INVARIANTES — ne jamais modifier/reformuler/augmenter
 * (cf. PAYLOADS_654.md). Seules les LETTRES suivent le tirage : elles sont
 * paramétrées ici plutôt que substituées après coup, car un remplacement du
 * seul motif `[AB]` laissait les « A et B » nus des options 2 et 3 intacts —
 * l'énoncé parlait de [PQ] et les réponses de A et B (99,7 % des tirages).
 */
const OPTIONS_RECONNAITRE = (A, B) => [
    `Oui, M est bien le milieu de [${A}${B}]`,
    `Non : M est équidistant de ${A} et ${B} mais n'appartient pas à [${A}${B}]`,
    `Non : M est sur [${A}${B}] mais pas équidistant de ${A} et ${B}`,
    // ⚠ DETTE CONNUE (relevée en revue, laissée sur arbitrage) — cette option
    // n'est JAMAIS la bonne réponse (0/5000 tirages mesurés). Les trois
    // scénarios du générateur donnent toujours de quoi trancher, et chacun
    // mappe sur l'une des trois options précédentes. Distracteur mort :
    // l'élève qui l'écarte passe à 1 chance sur 3 au lieu d'1 sur 4.
    // Correction : ajouter un 4e scénario où l'énoncé donne MA = MB sans
    // préciser si M appartient au segment. Une branche de plus, pas une refonte.
    'Non : on ne peut pas conclure avec ces informations',
];

const genReconnaitreMilieuQcm = () => {
    const [A, B] = pickN(2, LETTRES_SANS_M);
    const scenario = randomChoice(['milieu', 'mediatrice', 'quelconque']);

    // Les 4 mêmes options, dans le même ordre, à la formulation près identique :
    // seules les lettres suivent le tirage. Le shuffle de makeQcm garde l'invariance.
    const choix = OPTIONS_RECONNAITRE(A, B);

    // Les trois scénarios présentent leurs données dans le MÊME ordre — longueur
    // du segment, puis propriétés de M. Ce qui change d'un scénario à l'autre
    // saute alors aux yeux, et c'est justement ce que l'élève doit repérer.
    const question = `<p>M est-il le milieu de [${A}${B}] ?</p>`;
    let texte, correct;

    if (scenario === 'milieu') {
        // MA = MB, M ∈ [AB], AB = 2*MA
        const ma = randInt(2, 9);
        const ab = 2 * ma;
        texte = `<p>${A}${B} = ${ab} cm, M ∈ [${A}${B}], M${A} = ${ma} cm et M${B} = ${ma} cm.</p>${question}`;
        correct = choix[0];
    } else if (scenario === 'mediatrice') {
        // MA = MB, M ∉ [AB], AB < 2*MA (strict pour que la médiatrice soit plausible)
        // On garantit en plus un ratio raisonnable AB ≥ MA (sinon la figure
        // devient géométriquement aberrante : M très loin de [AB] par rapport à AB).
        const ma = randInt(4, 9);
        const minAb = Math.max(2, ma);
        const ab = randInt(minAb, 2 * ma - 2);
        texte = `<p>${A}${B} = ${ab} cm, M${A} = ${ma} cm, M${B} = ${ma} cm et M n'appartient pas à [${A}${B}].</p>${question}`;
        correct = choix[1];
    } else {
        // M ∈ [AB], MA + MB = AB, MA ≠ MB
        const ab = randInt(6, 12);
        let ma;
        do { ma = randInt(1, ab - 1); } while (ma * 2 === ab); // exclure ma = ab/2 (sinon milieu)
        const mb = ab - ma;
        texte = `<p>${A}${B} = ${ab} cm, M ∈ [${A}${B}], M${A} = ${ma} cm et M${B} = ${mb} cm.</p>${question}`;
        correct = choix[2];
    }

    const payload = makeQcm(choix, correct, texte, opts(L_RECONNAITRE));
    payload._meta.generatorKey = 'reconnaitre_milieu_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Identifier le placement correct (QCM 4 images SVG)
// ═══════════════════════════════════════════════════════════

/**
 * Codage d'égalité : un petit trait perpendiculaire au milieu d'une longueur.
 * Deux longueurs portant le même codage sont déclarées égales — c'est ce qui
 * permet à l'élève de RÉPONDRE sans mesurer. Sans lui, la figure n'affirmerait
 * rien et le mot « exactement » de l'énoncé serait sans fondement.
 *
 * En ROUGE, et non en noir : les repères d'extrémité en A et B sont eux aussi
 * des traits noirs perpendiculaires au segment. Deux notations de sens
 * différent dans le même trait se confondent. Le rouge est celui du coup de
 * pouce, pour que l'élève retrouve le symbole qu'on vient de lui expliquer.
 */
const COLOR_CODAGE = '#E24B4A';
const codageEgalite = (x1, y1, x2, y2) => {
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    const t = 4.5;
    return `<line x1="${(mx - px * t).toFixed(1)}" y1="${(my - py * t).toFixed(1)}"`
        + ` x2="${(mx + px * t).toFixed(1)}" y2="${(my + py * t).toFixed(1)}"`
        + ` stroke="${COLOR_CODAGE}" stroke-width="2" />`;
};

/**
 * Construit un SVG montrant le segment [AB] + un point M à une position donnée.
 * position ∈ { 'milieu', 'tiers', 'deuxTiers', 'mediatrice' }
 * sensPerp ∈ { -1, +1 } (au-dessus ou en dessous pour la médiatrice)
 *
 * Le codage d'égalité apparaît sur les figures « milieu » ET « médiatrice » —
 * les deux seules où les distances à A et B sont égales. Le porter uniquement
 * sur la bonne figure donnerait la réponse ; le porter sur les deux oblige à
 * vérifier la SECONDE condition, l'appartenance au segment.
 */
const buildPlacementSvg = (A, B, Ax, Ay, Bx, By, position, sensPerp) => {
    const dx = Bx - Ax;
    const dy = By - Ay;
    const AB = Math.hypot(dx, dy);
    const perpX = -dy / AB;
    const perpY = dx / AB;

    let Mx, My;
    if (position === 'milieu') {
        Mx = (Ax + Bx) / 2;
        My = (Ay + By) / 2;
    } else if (position === 'tiers') {
        Mx = Ax + dx / 3;
        My = Ay + dy / 3;
    } else if (position === 'deuxTiers') {
        Mx = Ax + (2 * dx) / 3;
        My = Ay + (2 * dy) / 3;
    } else {
        // médiatrice : au-dessus/dessous du milieu, offset ≈ 0,30 × AB
        const midX = (Ax + Bx) / 2;
        const midY = (Ay + By) / 2;
        const offset = 0.30 * AB;
        Mx = midX + sensPerp * offset * perpX;
        My = midY + sensPerp * offset * perpY;
    }

    // Sur la figure médiatrice UNIQUEMENT : on relie M aux deux extrémités en
    // pointillés. Sur les trois autres, M est SUR le segment — des pointillés s'y
    // superposeraient sans rien montrer. Ici ils rendent le piège lisible : les
    // deux distances sont visiblement égales, et l'élève doit se rappeler que
    // l'équidistance seule ne suffit pas. Tracés en premier pour passer SOUS le
    // segment, le point et les libellés.
    const pointilles = position === 'mediatrice'
        ? `<line x1="${Ax}" y1="${Ay}" x2="${Mx.toFixed(1)}" y2="${My.toFixed(1)}" stroke="#888" stroke-width="1" stroke-dasharray="3 3" />`
          + `<line x1="${Bx}" y1="${By}" x2="${Mx.toFixed(1)}" y2="${My.toFixed(1)}" stroke="#888" stroke-width="1" stroke-dasharray="3 3" />`
        : '';

    // Codage sur les deux figures où MA = MB, et sur elles seules.
    const codage = (position === 'milieu' || position === 'mediatrice')
        ? codageEgalite(Ax, Ay, Mx, My) + codageEgalite(Bx, By, Mx, My)
        : '';

    // viewBox fixe 180x120
    const body =
        pointilles
        + `<line x1="${Ax}" y1="${Ay}" x2="${Bx}" y2="${By}" stroke="#000" stroke-width="2" />`
        + `<line x1="${Ax}" y1="${Ay - 4}" x2="${Ax}" y2="${Ay + 4}" stroke="#000" stroke-width="2" />`
        + `<line x1="${Bx}" y1="${By - 4}" x2="${Bx}" y2="${By + 4}" stroke="#000" stroke-width="2" />`
        + codage
        + `<circle cx="${Mx.toFixed(1)}" cy="${My.toFixed(1)}" r="3.5" fill="#000" />`
        + `<text x="${Ax - 6}" y="${Ay - 8}" text-anchor="end" font-size="14" fill="#000">${A}</text>`
        + `<text x="${Bx + 6}" y="${By - 8}" font-size="14" fill="#000">${B}</text>`
        + `<text x="${(Mx + 6).toFixed(1)}" y="${(My - 6).toFixed(1)}" font-size="14" fill="#000">M</text>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180 120">${body}</svg>`;
};

const genPlacementMilieuImages = () => {
    const [A, B] = pickN(2, LETTRES_SANS_M);

    // Paramètres du segment dans le viewBox 180×120
    // AB ≈ 120 px, centré à (90, 60), orientation légèrement variable
    // Angles doux pour garder la distinction milieu / médiatrice visuellement lisible.
    const angleDeg = randomChoice([-8, -4, 0, 0, 0, 0, 4, 8]);
    const angle = (angleDeg * Math.PI) / 180;
    const abPx = randInt(110, 140);
    const cx = 90;
    const cy = 60 + randInt(-6, 6);
    const half = abPx / 2;
    const Ax = Math.round(cx - half * Math.cos(angle));
    const Ay = Math.round(cy - half * Math.sin(angle));
    const Bx = Math.round(cx + half * Math.cos(angle));
    const By = Math.round(cy + half * Math.sin(angle));

    const sensPerp = randomChoice([-1, 1]);

    const positions = ['milieu', 'tiers', 'deuxTiers', 'mediatrice'];
    // ⚠️ imageData (PAS imageUrl) pour les choix images générés : avec imageUrl,
    // l'élève émet l'URL seule mais resolveBonneReponse renvoie le JSON complet
    // du choix → mismatch garanti au scoring (réponse juste comptée fausse).
    // Sans imageUrl, les deux côtés retombent sur le même JSON.
    const images = positions.map(pos => {
        const svg = buildPlacementSvg(A, B, Ax, Ay, Bx, By, pos, sensPerp);
        return {
            type: 'image',
            config: { imageData: `data:image/svg+xml;base64,${svgToBase64(svg)}` },
        };
    });

    const correct = images[0]; // 'milieu' est toujours en position 0 avant shuffle
    const texte = `<p>Sur quelle figure le point M est-il <strong>exactement</strong> le milieu du segment [${A}${B}] ?</p>`;

    const payload = makeQcm(images, correct, texte, opts(L_PLACEMENT));
    payload._meta.generatorKey = 'placement_milieu_qcm_images';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Construire le milieu au compas (programme_construction)
// ═══════════════════════════════════════════════════════════

const genConstruireCompasProgramme = () => {
    const [A, B] = pickN(2, LETTRES_SANS_M);
    const cx = Math.round(VIEWBOX_PC.w / 2);
    const cy = Math.round(VIEWBOX_PC.h / 2);

    // Tirage avec rejet : les DEUX cercles complets (rayon rPx aux centres A et B)
    // doivent tenir dans le viewBox avec 10 px de marge — avec AB = 9 cm et
    // ratio 0,8 le rayon atteint 216 px et déborde forcément (cadre de 400 de haut).
    let abCm, rCm, rPx, angleDeg, Ax, Ay, Bx, By;
    let ok = false;
    for (let attempt = 0; attempt < 200 && !ok; attempt++) {
        abCm = randInt(5, 9);
        // Rayon : 0,6 à 0,8 × AB (garantit r > AB/2), arrondi au demi-cm lisible
        const ratio = randomChoice([0.6, 0.65, 0.7, 0.75, 0.8]);
        rCm = Math.round(abCm * ratio * 2) / 2;
        rPx = rCm * PX_PER_CM;
        // Orientation : horizontale (0°) ou oblique légère (-15° à +15°, pas 5°)
        angleDeg = randomChoice([-15, -10, -5, 0, 0, 0, 5, 10, 15]);
        const angle = (angleDeg * Math.PI) / 180;
        const half = (abCm * PX_PER_CM) / 2;
        Ax = Math.round(cx - half * Math.cos(angle));
        Ay = Math.round(cy - half * Math.sin(angle));
        Bx = Math.round(cx + half * Math.cos(angle));
        By = Math.round(cy + half * Math.sin(angle));
        const marge = 10;
        ok = Math.min(Ax, Bx) - rPx >= marge
            && Math.max(Ax, Bx) + rPx <= VIEWBOX_PC.w - marge
            && Math.min(Ay, By) - rPx >= marge
            && Math.max(Ay, By) + rPx <= VIEWBOX_PC.h - marge;
    }
    if (!ok) {
        // Fallback validé : AB = 6 cm, r = 4 cm, horizontal → cercles dans [30;570]×[80;320]
        abCm = 6;
        rCm = 4;
        rPx = rCm * PX_PER_CM;
        angleDeg = 0;
        Ax = cx - 3 * PX_PER_CM; Ay = cy;
        Bx = cx + 3 * PX_PER_CM; By = cy;
    }
    const Mx = Math.round((Ax + Bx) / 2);
    const My = Math.round((Ay + By) / 2);

    const points = [
        { name: A, x: Ax, y: Ay },
        { name: B, x: Bx, y: By },
    ];
    const preShapes = [
        { type: 'line', from: A, to: B },
    ];

    // Points d'intersection des deux cercles de même rayon : sur la médiatrice,
    // à distance h = √(r² − (AB/2)²) du milieu, de part et d'autre de (AB).
    // Le tirage garantit r ≥ 0,6 × AB > AB/2, donc h est toujours réel.
    const AB_px = Math.hypot(Bx - Ax, By - Ay);
    const h = Math.sqrt(Math.max(0, rPx ** 2 - (AB_px / 2) ** 2));
    const vx = -(By - Ay) / AB_px;
    const vy = (Bx - Ax) / AB_px;
    const inter1 = { x: Mx + h * vx, y: My + h * vy };
    const inter2 = { x: Mx - h * vx, y: My - h * vy };

    // 4 étapes, pas 3 : la médiatrice doit être TRACÉE avant qu'on puisse parler
    // de son intersection avec le segment. La version précédente passait des deux
    // cercles directement à M, en invoquant une médiatrice jamais construite.
    const tasks = [
        {
            id: 'a',
            consigne: `Trace en <b>bleu</b> le cercle de centre ${A} et de rayon ${fmt(rCm)} cm.`,
            color: COLOR_BLEU,
            shape: { type: 'circle', cx: Ax, cy: Ay, r: rPx },
        },
        {
            id: 'b',
            consigne: `Trace en <b>rouge</b> le cercle de centre ${B} et de rayon ${fmt(rCm)} cm (même rayon que le cercle précédent).`,
            color: COLOR_ROUGE,
            shape: { type: 'circle', cx: Bx, cy: By, r: rPx },
        },
        {
            id: 'c',
            kind: 'line',
            consigne: `Trace en <b>vert</b> la droite passant par les <b>deux points d'intersection</b> des deux cercles.`,
            color: COLOR_VERT,
            shape: { type: 'line', p1: inter1, p2: inter2 },
        },
        {
            id: 'd',
            consigne: `Place en <b>orange</b> le point M, intersection de cette droite avec le segment [${A}${B}].`,
            color: COLOR_ORANGE,
            shape: { type: 'circle', cx: Mx, cy: My, r: 0 },
            pointLabel: 'M',
        },
    ];

    const texte = `<p>Construis le milieu M du segment [${A}${B}] au compas et à la règle non graduée. Suis les 4 étapes dans l'ordre.</p>`;

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOLERANCE_PC,
            viewBox: VIEWBOX_PC,
            points,
            preShapes,
            tasks,
        },
        texte,
        opts(L_CONSTRUIRE_COMPAS),
    );
    payload._meta.generatorKey = 'construire_compas_programme';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    definition_vf_tableau:         genDefinitionVF,
    demilongueur_saisie:           genDemilongueurSaisie,
    reconnaitre_milieu_qcm_unique: genReconnaitreMilieuQcm,
    placement_milieu_qcm_images:   genPlacementMilieuImages,
    construire_compas_programme:   genConstruireCompasProgramme,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', qcm_images: 'QCM images',
    saisie: 'Saisie', texte: 'Saisie',
    tableau_vf: 'Vrai/Faux', vf_tableau: 'Vrai/Faux', vf_figure: 'Vrai/Faux',
    programme: 'Construction',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'definition',         label: L_DEFINITION,        formatsList: buildFormats('definition') },
    { id: 'demilongueur',       label: L_DEMILONGUEUR,      formatsList: buildFormats('demilongueur') },
    { id: 'reconnaitre_milieu', label: L_RECONNAITRE,       formatsList: buildFormats('reconnaitre_milieu') },
    { id: 'placement_milieu',   label: L_PLACEMENT,         formatsList: buildFormats('placement_milieu') },
    { id: 'construire_compas',  label: L_CONSTRUIRE_COMPAS, formatsList: buildFormats('construire_compas') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genDefinitionVF,
    genDemilongueurSaisie,
    genReconnaitreMilieuQcm,
    genPlacementMilieuImages,
    genConstruireCompasProgramme,
};
